import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const SYM = { GBP: '£', EUR: '€', USD: '$', CAD: 'CA$', AUD: 'A$' };

const emptyForm = { name: '', aws_region_code: '', default_currency: 'USD', sort_order: 99 };

export default function RegionsAdmin() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // region object or null (create)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminAPI.getAllRegions();
      setRegions(data || []);
    } catch { toast.error('Failed to load regions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, aws_region_code: r.aws_region_code, default_currency: r.default_currency, sort_order: r.sort_order });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.aws_region_code) return toast.error('Name and AWS region code are required');
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateRegion(editing.id, form);
        toast.success(`Region '${form.name}' updated`);
      } else {
        await adminAPI.createRegion(form);
        toast.success(`Region '${form.name}' created`);
      }
      setShowDialog(false);
      load();
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try {
      await adminAPI.updateRegion(r.id, { is_active: !r.is_active });
      toast.success(`Region '${r.name}' ${!r.is_active ? 'activated' : 'deactivated'}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-regions-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>AWS Regions</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>Manage hosting regions and their default currencies for new quotes.</p>
        </div>
        <Button onClick={openCreate} style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-2" data-testid="regions-new-button">
          <Plus size={15} /> Add Region
        </Button>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Region</TableHead>
              <TableHead>AWS Code</TableHead>
              <TableHead>Default Currency</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
            )) : regions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Globe size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
                  <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No regions configured yet.</p>
                </TableCell>
              </TableRow>
            ) : regions.map(r => (
              <TableRow key={r.id} style={{ opacity: r.is_active ? 1 : 0.5 }}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Globe size={14} style={{ color: 'hsl(var(--c4p-brand-blue))' }} />
                    <span className="font-medium">{r.name}</span>
                  </div>
                </TableCell>
                <TableCell><span className="font-mono text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{r.aws_region_code}</span></TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">
                    {SYM[r.default_currency] || ''} {r.default_currency}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{r.sort_order}</TableCell>
                <TableCell>
                  <Switch
                    checked={r.is_active}
                    onCheckedChange={() => toggleActive(r)}
                    data-testid={`region-active-${r.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="gap-1 text-xs">
                    <Pencil size={12} /> Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Region - ${editing.name}` : 'Add AWS Region'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Region Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. US East"
                required
                data-testid="region-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>AWS Region Code *</Label>
              <Input
                value={form.aws_region_code}
                onChange={e => setForm(f => ({ ...f, aws_region_code: e.target.value }))}
                placeholder="e.g. us-east-1"
                required
                data-testid="region-code-input"
              />
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Used for reference only - does not affect pricing.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Default Currency</Label>
                <Select value={form.default_currency} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                  <SelectTrigger data-testid="region-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{SYM[c]} {c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.sort_order}
                  onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 99 }))}
                  data-testid="region-sort-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} data-testid="region-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save Changes' : 'Add Region'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
